import localforage from 'localforage';
import {
  type Dispatch,
  type FC,
  type PropsWithChildren,
  type ReactNode,
  type SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';

import type { ProductTypeOptions } from '@/types/product';
import { getCheckoutLineUnitPriceWithoutDiscount } from '@/utils/checkout';

import { Box } from '@/components/Box';
import { PaymentMethodsLogo } from '@/components/ShoppingBag/PaymentMethodsLogo';
import type {
  CheckoutLineFragment,
  MoneyFragment,
  ProductMediaFragment,
  ProductThumbnailFragment,
  TaxedMoneyFragment,
} from '@/graphql/saleor/api';
import { useCheckout } from '@/hooks/useCheckout';
import { useFetchGwpName } from '@/hooks/useFetchGwpName';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useRouter } from '@/hooks/useRouter';
import { useToggle } from '@/hooks/useToggle';
import { useUser } from '@/hooks/useUser';
import { getLineSubheader } from '@/lib/checkout';
import { writeCheckoutIdCookie } from '@/lib/checkout/checkoutIdCookie';
import { CacheKey, GLOBAL_E_MARKET_AVAILABLE, VOUCHER_TYPE } from '@/lib/const';
import { genericErrorHandler } from '@/lib/forms/helpers';
import { EVENT_NAME } from '@/lib/gtm/const';
import { getTranslatedName } from '@/lib/i18n';
import { checkIsProductLineFree } from '@/lib/order';
import { sumPrices } from '@/lib/prices';
import { findMediaByShotType } from '@/lib/product';
import {
  checkIfAllTaxesExcluded,
  checkIfVatIncluded,
  getOrderTaxAmountLabel,
  getTotalTaxMessage,
} from '@/lib/regions';
import type { ApiErrorWithType, Maybe, WithChildren } from '@/lib/types';
import { commonMessages } from '@/messages/common';
import { LocaleContext } from '@/providers/LocaleProvider';
import { useRegionProvider } from '@/providers/RegionProvider';
import { checkoutActions } from '@/states/checkout';
import { GiftForm } from '@/views/DeliveryView/components/GiftForm';
import { giftUpdatePromise } from '@/views/DeliveryView/helpers';
import type { PackLinesProps } from '@/views/PayPerLinkPaymentView/components';
import { WarrantyImage } from '@/views/ShoppingBagView/components/ShoppingBagCard/components/WarrantyImage/WarrantyImage';

import { Checkbox } from '../Checkbox';
import { Complimentary } from '../Complimentary/Complimentary';
import { Flex } from '../Flex';
import type { NextImageProps } from '../NextImage';
import { NextImage } from '../NextImage';
import type { PriceSize } from '../Price';
import { SalesTaxBadge } from '../SalesTaxBadge';
import { CustomErrors } from '../ServerErrors';
import { Typography, type TypographyScales } from '../Typography';
import { Voucher } from '../Voucher/Voucher';

import * as M from './messages';
import * as S from './ShoppingBag.styles';

const ShoppingBag = ({ children }: WithChildren) => {
  return <>{children}</>;
};

const Title = () => (
  <S.Title data-test-id='ShoppingBagTitle'>{M.YourShoppingBag}</S.Title>
);

interface SummaryWrapperProps {
  showCompactSummary?: boolean;
  showPadding?: boolean;
}
const SummaryWrapper: FC<PropsWithChildren<SummaryWrapperProps>> = ({
  children,
  showCompactSummary,
  showPadding,
  ...props
}) => (
  <S.SummaryWrapper
    $showCompactSummary={showCompactSummary}
    $showPadding={showPadding}
    data-test-id='ShoppingBagSummaryWrapper'
    {...props}
  >
    {children}
  </S.SummaryWrapper>
);

interface PriceComponentProps {
  channel?: string;
  displayZero?: boolean; // NOSONAR
  isLoading?: boolean;
  price: Maybe<Omit<MoneyFragment, '__typename'>>;
  scale?: TypographyScales;
  size?: PriceSize;
}

const SubtotalPrice = ({
  isLoading = false,
  price,
  scale = 'h5',
  size = 'm',
  ...props
}: PriceComponentProps) => (
  <S.LabelValueWrapper data-test-id='ShoppingBagSubtotalPrice' {...props}>
    <S.Label data-test-id='ShoppingBagSubtotalPriceLabel' scale={scale}>
      {M.Subtotal}
    </S.Label>
    {isLoading ? (
      <S.ValueSkeleton data-testid='ShoppingBagSubtotalPriceValueSkeleton' />
    ) : (
      <S.Value
        data-test-id='ShoppingBagSubtotalPriceValue'
        price={price}
        scale={scale}
        size={size}
      />
    )}
  </S.LabelValueWrapper>
);

const ToPayLater = ({ price, ...props }: PriceComponentProps) => (
  <S.LabelValueWrapper {...props}>
    <S.Label>{M.ToPayLater}</S.Label>
    <S.Value price={price} />
  </S.LabelValueWrapper>
);

const ToPayNow = ({ price, ...props }: PriceComponentProps) => (
  <S.LabelValueWrapper data-test-id='ShoppingBagToPayNow' isBold {...props}>
    <S.Label data-test-id='ShoppingBagToPayNowLabel'>{M.ToPayNow}</S.Label>
    <S.Value data-test-id='ShoppingBagToPayNowValue' price={price} />
  </S.LabelValueWrapper>
);

const ShippingPrice = ({
  isLoading = false,
  price,
  scale = 'h5',
  size = 'm',
  ...props
}: PriceComponentProps) => {
  const deliveryFee =
    price?.amount === 0 ? (
      <Typography scale={scale}>{M.Free}</Typography>
    ) : (
      <S.Value price={price} scale={scale} size={size} />
    );

  return (
    <S.LabelValueWrapper data-test-id='ShoppingBagShippingPrice' {...props}>
      <S.Label scale={scale}>{M.DeliveryFee}</S.Label>
      {isLoading ? (
        <S.ValueSkeleton data-testid='ShoppingBagShippingPriceValueSkeleton' />
      ) : (
        <S.DeliveryFeeFreeValue data-test-id='ShoppingBagShippingPriceDeliveryFeeFreeValue'>
          {deliveryFee}
        </S.DeliveryFeeFreeValue>
      )}
    </S.LabelValueWrapper>
  );
};

interface TotalPriceProps extends PriceComponentProps {
  channel?: string;
  isCheckoutSelectedAddress?: boolean;
  netPrice?: Maybe<Omit<MoneyFragment, '__typename'>>;
  withBottomBorder?: boolean;
  withTaxCalculationInfo?: boolean;
}

const TotalPrice = ({
  channel,
  isCheckoutSelectedAddress = true,
  isLoading = false,
  netPrice,
  price,
  withBottomBorder,
  withTaxCalculationInfo = true,
  ...props
}: TotalPriceProps) => {
  const { formatMessage } = useIntl();
  const shouldShowSalesTaxInfo =
    !checkIfVatIncluded(channel) && !checkIfAllTaxesExcluded(channel);
  const totalTaxMessage = getTotalTaxMessage(
    netPrice,
    price,
    channel,
    isCheckoutSelectedAddress,
  );

  return (
    <S.TotalWrapper
      data-test-id='ShoppingBagTotalPrice'
      withBottomBorder={withBottomBorder}
      {...props}
    >
      <Flex justifyContent='space-between'>
        <S.TotalLabel data-test-id='ShoppingBagTotalPriceLabel'>
          {M.Total}
        </S.TotalLabel>
        {isLoading ? (
          <S.TotalValueSkeleton data-testid='ShoppingBagTotalPriceValueSkeleton' />
        ) : (
          <S.TotalValue
            data-test-id='ShoppingBagTotalPriceValue'
            displayZero={true}
            price={price}
          />
        )}
      </Flex>
      {withTaxCalculationInfo ? (
        <S.SalesTaxCalculatedAtCheckout data-test-id='ShoppingBagSalesTaxInfo'>
          {totalTaxMessage && formatMessage(totalTaxMessage)}
        </S.SalesTaxCalculatedAtCheckout>
      ) : (
        <>
          {shouldShowSalesTaxInfo && (
            <SalesTaxInfo data-testid='ShoppingBagShippingSalesTaxInfo' />
          )}
        </>
      )}
    </S.TotalWrapper>
  );
};

const RetailDeliveryFeesTax = ({ price, ...props }: PriceComponentProps) => (
  <S.LabelValueWrapper
    data-test-id='ShoppingBagRetailDeliveryFeesTax'
    {...props}
  >
    <S.Label data-test-id='ShoppingBagRetailDeliveryFeesTaxLabel' scale='h5'>
      {M.RetailDeliveryFees}
    </S.Label>
    <S.Value
      data-test-id='ShoppingBagRetailDeliveryFeesTaxValue'
      price={price}
      scale='h6'
      size='m'
    />
  </S.LabelValueWrapper>
);

const SalesTax = ({
  channel,
  displayZero,
  isLoading = false,
  price,
  ...props
}: PriceComponentProps) => {
  const { formatMessage } = useIntl();
  const taxLabel = getOrderTaxAmountLabel(channel);
  return (
    <S.LabelValueWrapper data-test-id='ShoppingBagSalesTax' {...props}>
      <S.Label data-test-id='ShoppingBagSalesTaxLabel' scale='h5'>
        {taxLabel && formatMessage(taxLabel)}
      </S.Label>
      {isLoading ? (
        <S.ValueSkeleton data-testid='ShoppingBagSalesTaxValueSkeleton' />
      ) : (
        <S.Value
          data-test-id='ShoppingBagSalesTaxValue'
          displayZero={displayZero}
          price={price}
          scale='h6'
          size='m'
        />
      )}
    </S.LabelValueWrapper>
  );
};

interface DepositProps {
  depositPercentage: Maybe<number>;
  price: Maybe<Omit<MoneyFragment, '__typename'>>;
  type: 'pay now' | 'already paid';
}
interface AlreadyPaidProps {
  price: Maybe<Omit<MoneyFragment, '__typename'>>;
  type: 'pay now' | 'already paid';
}

const Deposit = ({
  depositPercentage,
  price,
  type,
  ...props
}: DepositProps) => (
  <S.LabelValueWrapper
    data-test-id={`ShoppingBagDeposit-${type}`}
    isBold={type === 'pay now'}
    {...props}
  >
    <S.Label data-test-id={`ShoppingBagDepositLabel-${type}`}>
      {type === 'pay now' ? M.PayNowDeposit : M.AlreadyPaidDeposit}{' '}
      {depositPercentage}%
    </S.Label>
    <Flex data-test-id={`ShoppingBagDepositValueWrapper-${type}`}>
      <S.Value data-test-id={`ShoppingBagDepositValue-${type}`} price={price} />
    </Flex>
  </S.LabelValueWrapper>
);

const AlreadyPaidAmount = ({ price, type, ...props }: AlreadyPaidProps) => (
  <S.LabelValueWrapper
    data-test-id={`ShoppingBagAlreadyPaid-${type}`}
    isBold={false}
    {...props}
  >
    <S.Label data-test-id='ShoppingBagTransactionSummaryLabel'>
      {M.AlreadyPaidAmount}
    </S.Label>
    <Flex data-test-id={`ShoppingBagDepositValueWrapper-${type}`}>
      <S.Value data-test-id={`ShoppingBagDepositValue-${type}`} price={price} />
    </Flex>
  </S.LabelValueWrapper>
);

const PreOrderNote = ({ ...props }) => (
  <S.Description data-test-id='ShoppingBagPreOrderNote' {...props}>
    {M.PreOrderNote}
  </S.Description>
);

const ShippingAndTaxNote = ({ ...props }) => (
  <S.ShippingAndTaxesNote
    data-test-id='ShoppingBagShippingAndTaxNote'
    {...props}
  >
    {M.ShippingAndTaxesNote}
  </S.ShippingAndTaxesNote>
);

const SalesTaxInfo = ({ ...props }) => (
  <S.SalesTaxInfo {...props}>{M.SalesTaxInfo}</S.SalesTaxInfo>
);

interface ButtonsProps {
  areBraceletSizeErrorsSet: () => boolean;
  checkoutId?: string;
  coupon?: string;
  isDisabled: boolean;
  lines?: CheckoutLineFragment[];
  setCheckoutValidationErrors?: Dispatch<SetStateAction<ApiErrorWithType[]>>;
  totalBalance?: number;
}

const Buttons = ({
  areBraceletSizeErrorsSet,
  checkoutId,
  coupon,
  isDisabled,
  lines,
  setCheckoutValidationErrors,
  totalBalance,
  ...props
}: ButtonsProps) => {
  const { formatMessage } = useIntl();
  const { locale, paths, push } = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const { sendEcommerceEventByLines, sendEvent } = useGoogleAnalytics();
  const {
    snapshot: { isAuthenticated },
  } = useUser();
  const { giftDelete, giftUpdate } = useCheckout<true>();

  const { getValues, setError } = useFormContext();
  const giftFormData = getValues('giftForm');

  const validateCheckout = async (): Promise<boolean> => {
    if (
      !setCheckoutValidationErrors ||
      !GLOBAL_E_MARKET_AVAILABLE?.includes(locale)
    ) {
      return true;
    }

    const genericError: ApiErrorWithType = {
      type: 'checkout',
      message: formatMessage(commonMessages.genericError),
      code: 'INVALID',
    };

    if (!checkoutId) {
      setCheckoutValidationErrors([genericError]);
      return false;
    }

    try {
      const response = await fetch(paths.api.checkoutValidate.asPath(), {
        method: 'POST',
        body: JSON.stringify({ id: checkoutId }),
      });
      const data = await response.json();

      if (!response.ok) {
        setCheckoutValidationErrors([genericError]);
        return false;
      }

      if (Array.isArray(data?.errors) && data.errors.length > 0) {
        setCheckoutValidationErrors(data.errors);
        return false;
      }
    } catch {
      setCheckoutValidationErrors([genericError]);
      return false;
    }

    return true;
  };

  const handleClick = async () => {
    setIsLoading(true);

    try {
      if (giftFormData) {
        await giftUpdatePromise(
          true,
          giftFormData.from,
          giftFormData.message,
          giftUpdate,
          giftDelete,
          genericErrorHandler({
            formErrorsHandler: setError,
            formatMessage,
            apiErrorsHandler: checkoutActions.setErrors,
            errorType: 'checkout',
          }),
        );
      }

      const isCheckoutValid = await validateCheckout();
      if (!isCheckoutValid || !areBraceletSizeErrorsSet()) {
        return;
      }

      const localCheckoutId = await localforage.getItem(CacheKey.CheckoutId);
      if (checkoutId && checkoutId !== localCheckoutId) {
        await localforage.setItem(CacheKey.CheckoutId, checkoutId);
        writeCheckoutIdCookie(checkoutId);
      }

      if (lines?.length) {
        sendEcommerceEventByLines({
          coupon,
          event: EVENT_NAME.BEGIN_CHECKOUT,
          lines,
          totalValue: totalBalance,
        });
        if (!isAuthenticated) {
          sendEvent({ event: EVENT_NAME.GUEST_CHECKOUT });
        }
      }

      await push(paths.store.checkout.delivery.url());
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <S.CheckoutButton
      data-test-id='ShoppingBagCheckoutButton'
      disabled={isDisabled || isLoading}
      onClick={handleClick}
      {...props}
    >
      {formatMessage(commonMessages.checkout)}
    </S.CheckoutButton>
  );
};

export const ProductImage = ({
  $productCanPurchase = true,
  alt,
  height,
  src,
  width,
  ...props
}: Pick<NextImageProps, 'alt' | 'height' | 'src' | 'width'> & {
  $productCanPurchase?: boolean;
}) => {
  const { formatMessage } = useIntl();
  return (
    <S.ProductImageWrapper {...props}>
      <NextImage
        alt={alt}
        height={height}
        src={src}
        style={{ objectFit: 'contain' }}
        width={width}
      />
      {!$productCanPurchase && (
        <S.ProductImageOverlay>
          {formatMessage(commonMessages.outOfStock)}
        </S.ProductImageOverlay>
      )}
    </S.ProductImageWrapper>
  );
};

interface PaymentsSectionProps {
  billingAddressCountryCode?: string;
  hasWatch?: boolean;
  id?: string;
  isDepositPaid?: boolean;
  isPreOrder?: boolean;
  locale?: string;
  showCompactSummary?: boolean;
  showLogos?: boolean;
  totalPrice?: number;
}

const PaymentMethodsSection = ({
  billingAddressCountryCode,
  hasWatch = false,
  id,
  isDepositPaid,
  isPreOrder,
  showCompactSummary = false,
  showLogos = true,
  totalPrice,
  ...props
}: PaymentsSectionProps) => {
  const locale = useContext(LocaleContext);

  return (
    <Box mt={4}>
      <S.AllTransactionAreSafeAndSecure
        $showCompactSummary={showCompactSummary}
        data-test-id='ShoppingBagAllTransactionsSafety'
        {...props}
      >
        {M.AllTransactionsAreSafeAndSecure}
      </S.AllTransactionAreSafeAndSecure>

      {showLogos && locale && !GLOBAL_E_MARKET_AVAILABLE?.includes(locale) && (
        <PaymentMethodsLogo
          billingAddressCountryCode={billingAddressCountryCode}
          hasWatch={hasWatch}
          id={id}
          isDepositPaid={isDepositPaid}
          isPreOrder={isPreOrder}
          locale={locale}
          totalPrice={totalPrice}
        />
      )}
      <S.EncryptedPayment
        $showCompactSummary={showCompactSummary}
        data-test-id='ShoppingBagEncryptedPayment'
      >
        {showCompactSummary
          ? M.SSLEncryptedPaymentInline
          : M.SSLEncryptedPayment}
      </S.EncryptedPayment>
    </Box>
  );
};

interface LineProps extends ProductThumbnailFragment {
  buckle?: CheckoutLineFragment;
  channel?: string;
  children?: ReactNode;
  customErrorMessage?: string;
  hasExtendWarranty?: boolean;
  id?: string;
  index: number;
  isBuckleChild?: boolean;
  isChild?: boolean;
  media?: Maybe<ProductMediaFragment[]>;
  name: string;
  packLines?: PackLinesProps[];
  price: Maybe<TaxedMoneyFragment>;
  productType?: string;
  showCompactSummary?: boolean;
  subheader: string;
  voucherType?: string;
  warrantyExtensionDuration?: string | null;
}

const Line = ({
  buckle,
  channel,
  children,
  customErrorMessage,
  id,
  index,
  isBuckleChild = false,
  isChild = false,
  media,
  name,
  packLines,
  price,
  productType,
  showCompactSummary = false,
  subheader,
  thumbnail,
  voucherType,
  warrantyExtensionDuration,
  ...props
}: LineProps) => {
  const {
    query: { languageCode },
  } = useRegionProvider();
  const translatedName = useFetchGwpName(
    languageCode,
    id,
    productType,
    channel,
    name,
  );
  const buckleUnitPriceWithoutDiscount = buckle
    ? getCheckoutLineUnitPriceWithoutDiscount(buckle, voucherType)
    : undefined;

  const productPrice =
    sumPrices(price, buckleUnitPriceWithoutDiscount) ?? price;
  const isWarranty = productType === 'warranty';
  const isProductFree = checkIsProductLineFree(voucherType, price, channel);

  // Always prefer Soldier image for shopping bag lines
  const soldierImage = findMediaByShotType(media ?? [], 'Soldier');
  const displayImage = soldierImage ?? thumbnail;
  const imageSrc = displayImage?.url ?? '';
  const imageAlt = displayImage?.alt ?? '';
  const productVisual = (() => {
    if (isWarranty && warrantyExtensionDuration) {
      return (
        <WarrantyImage
          data-test-id={`ShoppingBagLineWarrantyImage-${index}`}
          height='48px'
          warrantyExtensionDuration={warrantyExtensionDuration}
          width='48px'
        />
      );
    }

    if (displayImage) {
      return (
        <ProductImage
          $productCanPurchase={!customErrorMessage}
          alt={imageAlt}
          data-test-id={`ShoppingBagLineProductImage-${index}`}
          height={isBuckleChild ? 40 : 48}
          src={imageSrc}
          width={isBuckleChild ? 40 : 48}
        />
      );
    }

    return (
      <S.PlaceHolderImage productType={productType as ProductTypeOptions} />
    );
  })();

  return (
    <>
      <S.Wrapper
        $showCompactSummary={showCompactSummary}
        data-test-id={`ShoppingBagLine-${index}`}
        isBuckleChild={isBuckleChild}
        isChild={isChild}
        {...props}
      >
        <S.BorderWrapper isBuckleChild={isBuckleChild} isChild={isChild}>
          <Flex>
            {productVisual}
            <Flex alignItems='flex-end' flexDirection='column' width='100%'>
              <Flex justifyContent='space-between' width='100%'>
                <S.TextWrapper>
                  <S.Name
                    $isBuckleChild={isBuckleChild}
                    $productCanPurchase={!customErrorMessage}
                    $showCompactSummary={showCompactSummary}
                    data-test-id={`ShoppingBagLineName-${index}`}
                  >
                    {isWarranty && warrantyExtensionDuration
                      ? `${translatedName} (${warrantyExtensionDuration})`
                      : translatedName}
                  </S.Name>
                  {subheader &&
                    !['strap pack', 'tool']?.includes(
                      productType as string,
                    ) && (
                      <S.SmallText
                        $productCanPurchase={!customErrorMessage}
                        $showCompactSummary={showCompactSummary}
                        data-test-id={`ShoppingBagLineSubheader-${index}`}
                      >
                        {subheader}
                      </S.SmallText>
                    )}
                </S.TextWrapper>
                {!isBuckleChild && (
                  <S.PriceColumn $showCompactSummary={showCompactSummary}>
                    <Flex alignItems='flex-end' flexDirection='column'>
                      {productType === 'gift' ? (
                        <Complimentary />
                      ) : (
                        <>
                          <S.Price
                            $productCanPurchase={!customErrorMessage}
                            $showCompactSummary={showCompactSummary}
                            channel={channel}
                            data-test-id={`ShoppingBagLinePrice-${index}`}
                            displayZero={
                              voucherType !== VOUCHER_TYPE.PERCENTAGE
                            }
                            price={productPrice}
                          />
                          {!isProductFree && (
                            <SalesTaxBadge
                              channel={channel}
                              data-test-id={`ShoppingBagLineSalesTaxBadge-${index}`}
                              disabled={!customErrorMessage}
                            />
                          )}
                        </>
                      )}
                    </Flex>
                  </S.PriceColumn>
                )}
              </Flex>
            </Flex>
          </Flex>
          {customErrorMessage && (
            <CustomErrors message={customErrorMessage}></CustomErrors>
          )}
          {children}
          {packLines?.map(
            (
              { buckleType, id, packLines: buckles, unitPrice, variant },
              index,
            ) => {
              return (
                <Line
                  key={id}
                  channel={channel}
                  data-test-id={`ShoppingBagLine-${index}`}
                  index={index}
                  isBuckleChild={buckleType}
                  isChild={true}
                  name={getTranslatedName('name', variant?.product)}
                  packLines={buckles}
                  price={unitPrice}
                  productType={variant?.product.productType.name.toLowerCase()}
                  showCompactSummary={showCompactSummary}
                  subheader={getLineSubheader(variant?.product, variant?.sku)}
                  thumbnail={variant?.product?.thumbnail}
                />
              );
            },
          )}
        </S.BorderWrapper>
      </S.Wrapper>
    </>
  );
};

const Discount = ({
  price,
  scale = 'h5',
  size = 'm',
  ...props
}: PriceComponentProps) => {
  return (
    <S.LabelValueWrapper data-test-id='ShoppingBagDiscount' {...props}>
      <S.Label data-test-id='ShoppingBagDiscountLabel' scale={scale}>
        {M.Discount}
      </S.Label>
      <S.Value
        data-test-id='ShoppingBagDiscountValue'
        price={price}
        scale={scale}
        size={size}
      />
    </S.LabelValueWrapper>
  );
};

const Gift = () => {
  const { formatMessage } = useIntl();
  const [isGift, toggleGift] = useToggle(false);
  const { watch } = useFormContext();
  const [giftMessage] = watch(['giftForm.message']);

  return (
    <S.GiftWrapper data-testid='ShoppingBagGift'>
      <Checkbox onChange={toggleGift}>
        {formatMessage(commonMessages.thisIsAGift)}
      </Checkbox>
      {isGift && (
        <GiftForm
          data-testid='ShoppingBagGiftForm'
          message={giftMessage ?? ''}
          schemaPrefix='giftForm'
        />
      )}
    </S.GiftWrapper>
  );
};

interface ExpressSectionProps extends PropsWithChildren {
  loadingFallback?: ReactNode;
  presentation?: 'auto' | 'hidden' | 'loading' | 'ready';
}

const ExpressSection: FC<ExpressSectionProps> = ({
  children,
  loadingFallback,
  presentation = 'auto',
}) => {
  const { formatMessage } = useIntl();
  const rowRef = useRef<HTMLDivElement | null>(null);
  const [hasRenderedButton, setHasRenderedButton] = useState(false);

  useEffect(() => {
    if (presentation !== 'loading') return;

    const row = rowRef.current;
    if (!row) return;

    row.setAttribute('inert', '');
    const activeElement = row.ownerDocument.activeElement;
    if (activeElement instanceof HTMLElement && row.contains(activeElement)) {
      activeElement.blur();
    }

    return () => row.removeAttribute('inert');
  }, [presentation]);

  useEffect(() => {
    if (presentation !== 'auto') return;

    const el = rowRef.current;
    if (!el) return;

    const updateHasContent = () => {
      // Consider content present only if any direct child has at least one descendant
      const hasDeepDescendants = Array.from(el.children).some((child) => {
        return child.querySelector('*') !== null;
      });
      setHasRenderedButton(hasDeepDescendants);
    };

    updateHasContent();
    const observer = new MutationObserver(() => {
      updateHasContent();
    });
    observer.observe(el, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'hidden', 'aria-hidden'],
    });
    return () => observer.disconnect();
  }, [children, presentation]);

  if (presentation === 'hidden') return null;

  if (presentation !== 'auto') {
    const isLoading = presentation === 'loading';

    return (
      <S.ExpressSection>
        <S.ExpressTitle>
          {formatMessage(M.messages.expressCheckout)}
        </S.ExpressTitle>
        <S.ExpressStage>
          {isLoading && loadingFallback}
          <S.ExpressRow
            ref={rowRef}
            $initializing={isLoading}
            $visible={true}
            aria-hidden={isLoading}
          >
            {children}
          </S.ExpressRow>
        </S.ExpressStage>
      </S.ExpressSection>
    );
  }

  return (
    <S.ExpressSection>
      {hasRenderedButton && (
        <S.ExpressTitle>
          {formatMessage(M.messages.expressCheckout)}
        </S.ExpressTitle>
      )}
      <S.ExpressRow ref={rowRef} $visible={hasRenderedButton}>
        {children}
      </S.ExpressRow>
    </S.ExpressSection>
  );
};

ShoppingBag.Title = Title;
ShoppingBag.PrivilegeOffer = Voucher;
ShoppingBag.SummaryWrapper = SummaryWrapper;
ShoppingBag.SubtotalPrice = SubtotalPrice;
ShoppingBag.RetailDeliveryFeesTax = RetailDeliveryFeesTax;
ShoppingBag.ShippingPrice = ShippingPrice;
ShoppingBag.TotalPrice = TotalPrice;
ShoppingBag.SalesTax = SalesTax;
ShoppingBag.PreOrderNote = PreOrderNote;
ShoppingBag.Deposit = Deposit;
ShoppingBag.ShippingAndTaxNote = ShippingAndTaxNote;
ShoppingBag.SalesTaxInfo = SalesTaxInfo;
ShoppingBag.Buttons = Buttons;
ShoppingBag.PaymentMethodsSection = PaymentMethodsSection;
ShoppingBag.Line = Line;
ShoppingBag.ToPayLater = ToPayLater;
ShoppingBag.ToPayNow = ToPayNow;
ShoppingBag.Discount = Discount;
ShoppingBag.Gift = Gift;
ShoppingBag.AlreadyPaidAmount = AlreadyPaidAmount;
ShoppingBag.ExpressSection = ExpressSection;
ShoppingBag.displayName = 'ShoppingBag';

export { ShoppingBag };
